import React from 'react';
import { DatePicker, type DatePickerProps } from '@fluentui/react-datepicker-compat';

/** Formats a Date as MM/DD/YYYY for the DatePicker input. */
export const formatDateMDY = (date?: Date): string => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
};

/** Parses MM/DD/YYYY (or M/D/YYYY) back to a Date; returns null if invalid. */
export const parseDateMDY = (value: string): Date | null => {
  const match = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(value);
  if (!match) return null;
  const [, m, d, y] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
};

export interface AlertDatePickerProps
  extends Omit<
    DatePickerProps,
    | 'value'
    | 'onChange'
    | 'onSelectDate'
    | 'formatDate'
    | 'parseDateFromString'
    | 'allowTextInput'
  > {
  value: Date | null;
  onChange: (date: Date | null) => void;
}

/**
 * Alert-form-specific DatePicker: MM/DD/YYYY display + parse, text input
 * allowed, placeholder pre-set. Callers pass value + onChange (Date | null)
 * plus any standard DatePicker overrides such as `minDate` / `id`.
 */
export const AlertDatePicker: React.FC<AlertDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  ...rest
}) => (
  <DatePicker
    {...rest}
    value={value}
    onSelectDate={(d) => onChange(d ?? null)}
    placeholder={placeholder}
    allowTextInput
    formatDate={formatDateMDY}
    parseDateFromString={parseDateMDY}
  />
);
